import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getCOReview, respondToCO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SignaturePad } from "@/components/SignaturePad";
import { CheckCircle, XCircle, FileText, Loader2, FileDown } from "lucide-react";
import { format } from "date-fns";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { rollUpLineItems } from "@/lib/rollUpLineItems";
import { foldHiddenTaxIntoRows, NO_MATERIAL_MSG } from "@/lib/hideTax";
import { toast } from "sonner";

const fmt = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function COReviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState(null);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showSignature, setShowSignature] = useState(false);
  const [signedBy, setSignedBy] = useState("");

  useEffect(() => {
    document.title = "ServiceOps Change Order";
    (async () => {
      try {
        const res = await getCOReview(token);
        setData(res.data);
        const coNum = res.data?.co?.co_number;
        document.title = coNum ? `ServiceOps Change Order — ${coNum}` : "ServiceOps Change Order";
        if (res.data.co.status === "approved") setResponded("approved");
        if (res.data.co.status === "declined") setResponded("declined");
        if (res.data.co.status === "not_approved") setResponded("not_approved");
      } catch {
        setError("This change order link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const generateExecutedCOPdf = async (co, signatureData) => {
    const { default: jsPDF } = await import("jspdf");
    const { default: autoTable } = await import("jspdf-autotable");
    const { addCompanyHeader } = await import("@/lib/pdfHeader");
    const doc = new jsPDF();

    const headerY = addCompanyHeader(doc, 10);
    doc.setFontSize(16);
    doc.text("CHANGE ORDER", 14, headerY + 4);
    doc.setFontSize(10);
    doc.text(`CO# ${co.co_number}`, 14, headerY + 12);
    doc.text(`WO# ${data.wo_number}`, 14, headerY + 18);
    doc.text(`Date: ${format(new Date(), "MMM d, yyyy")}`, 14, headerY + 24);

    // BILL TO / SITE / ADDRESS / PROJECT 2x2
    const rightX = 200;
    let rY = headerY + 4;
    doc.setFontSize(8); doc.setTextColor(100, 100, 100); doc.setFont(undefined, "bold");
    doc.text("BILL TO", rightX, rY, { align: "right" });
    doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(0, 0, 0);
    doc.text(data.client_name || "—", rightX, rY + 5, { align: "right" });
    rY += 12;
    doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
    doc.text("SITE", rightX, rY, { align: "right" });
    doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(0, 0, 0);
    doc.text(data.site_name || "—", rightX, rY + 5, { align: "right" });
    doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
    doc.text("ADDRESS", rightX, rY + 11, { align: "right" });
    doc.setFontSize(9); doc.setFont(undefined, "normal"); doc.setTextColor(0, 0, 0);
    doc.text(data.site_address || "—", rightX, rY + 16, { align: "right", maxWidth: 90 });
    doc.setTextColor(0, 0, 0);

    let pY = headerY + 40;
    doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
    doc.text("PROJECT", 14, pY);
    doc.setFontSize(11); doc.setFont(undefined, "normal"); doc.setTextColor(0, 0, 0);
    doc.text(data.wo_title || co.description || "—", 14, pY + 6);

    // Roll up CO line items into one row per item. When the WO has
    // show_breakdown=ON, Material and Labor are kept as SEPARATE COLUMNS
    // (not extra rows) so the customer sees a clean per-item view.
    const breakout = !!co.show_breakdown;
    let lineItems = rollUpLineItems(co.line_items || [], breakout, true);
    // iter310 — hide-tax job: fold the tax into Material before printing.
    if (co.hide_tax && (co.tax_amount || 0) > 0) {
      const f = foldHiddenTaxIntoRows(lineItems, co.tax_amount, { materialKey: "material_total" });
      if (!f.ok) { toast.error(NO_MATERIAL_MSG); return; }
      lineItems = f.rows;
    }
    const headRow = breakout
      ? [["Part #", "Description", "Qty", "Material", "Freight", "Labor", "Total"]]
      : [["Part #", "Description", "Qty", "Total"]];
    const rows = lineItems.map(li => breakout
      ? [
          li.part_number || "",
          li.install_description ? `${li.name || li.description || ""}\n${li.install_description}` : (li.name || li.description || ""),
          (li.quantity ?? 1).toString(),
          fmt(li.material_total || 0),
          fmt(li.freight_total || 0),
          fmt(li.labor_total || 0),
          fmt(li.total || 0),
        ]
      : [
          li.part_number || "",
          li.install_description ? `${li.name || li.description || ""}\n${li.install_description}` : (li.name || li.description || ""),
          (li.quantity ?? 1).toString(),
          fmt(li.total || 0),
        ]);
    autoTable(doc, {
      startY: headerY + 60,
      head: headRow,
      body: rows,
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [148, 163, 184] },
    });

    let y = doc.lastAutoTable.finalY + 10;
    const subtotal = co.subtotal ?? (co.line_items || []).reduce((s, li) => s + (li.quantity || 0) * (li.unit_price || 0), 0);
    const taxAmount = co.tax_amount ?? 0;
    const total = co.total ?? subtotal;
    const taxRate = co.tax_rate ?? 0;
    doc.setFontSize(10);
    doc.text(`Subtotal:`, 140, y, { align: "right" });
    doc.text(fmt(co.hide_tax ? subtotal + taxAmount : subtotal), 190, y, { align: "right" });
    y += 6;
    if (!co.hide_tax) {
      doc.text(`Tax (${taxRate}%):`, 140, y, { align: "right" });
      doc.text(fmt(taxAmount), 190, y, { align: "right" });
    }
    y += 6;
    doc.setFontSize(12); doc.setFont(undefined, "bold");
    doc.text(`Change Order Total:`, 140, y, { align: "right" });
    doc.text(fmt(total), 190, y, { align: "right" });
    doc.setFont(undefined, "normal");

    y += 14;
    doc.setFontSize(10);
    doc.text("Customer Signature:", 14, y);
    y += 4;
    doc.addImage(signatureData, "PNG", 14, y, 60, 25);
    y += 28;
    doc.text(`Signed by: ${signedBy || "N/A"}`, 14, y);
    y += 5;
    doc.text(`Date: ${format(new Date(), "MMM d, yyyy h:mm a")}`, 14, y);

    return doc.output("datauristring");
  };

  const handleApproveWithSignature = async (signatureData) => {
    setResponding(true);
    try {
      const { co } = data;
      const executedPdf = await generateExecutedCOPdf(co, signatureData);
      await respondToCO(token, {
        action: "approve",
        signature_data: signatureData,
        signed_by: signedBy,
        executed_co_pdf: executedPdf,
      });
      setResponded("approved");
      setData(prev => ({
        ...prev,
        co: { ...prev.co, signature_data: signatureData, signed_by: signedBy, executed_co_pdf: executedPdf },
      }));
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit approval.");
    } finally {
      setResponding(false);
    }
  };

  const handleDecline = async () => {
    setResponding(true);
    try {
      await respondToCO(token, { action: "decline", reason: declineReason });
      setResponded("declined");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit response.");
    } finally {
      setResponding(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Error</h2>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  const { co, wo_number, wo_title, client_name, site_name, site_address } = data;
  // Roll up CO items (one row per equipment item). When show_breakdown is ON,
  // Material and Labor appear as separate COLUMNS — not extra rows.
  const coBreakout = !!co.show_breakdown;
  const rolledCoLines = rollUpLineItems(co.line_items || [], coBreakout, true);
  const lineItems = (co.hide_tax && (co.tax_amount || 0) > 0)
    ? (foldHiddenTaxIntoRows(rolledCoLines, co.tax_amount, { materialKey: "material_total" }).rows)
    : rolledCoLines;
  const subtotal = co.subtotal ?? lineItems.reduce((s, li) => s + (li.total || 0), 0);
  const taxRate = co.tax_rate ?? 0;
  const taxAmount = co.tax_amount ?? 0;
  const grandTotal = co.total ?? subtotal;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="co-review-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-sm px-8 pt-8 pb-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-5 h-5 text-amber-600" />
                <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">{co.co_number}</span>
                <span className="text-xs text-slate-400 uppercase tracking-wide">Change Order Review</span>
              </div>
              <h1 className="text-2xl font-bold text-slate-800">{co.description || "Change Order"}</h1>
            </div>
            <div className="text-right text-xs text-slate-500 leading-relaxed">
              <div className="font-semibold text-slate-700 text-sm">{COMPANY_INFO.name}</div>
              <div>{COMPANY_INFO.fullAddress}</div>
              <div>Phone: {COMPANY_INFO.phone}</div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-400 text-xs uppercase tracking-wide">Work Order</span>
              <br /><span className="font-semibold text-slate-700">{wo_number} — {wo_title}</span>
            </div>
            <div className="text-right">
              <span className="text-slate-400 text-xs uppercase tracking-wide">Customer</span>
              <br /><span className="font-medium text-slate-700">{client_name}</span>
            </div>
            {site_name && (
              <div>
                <span className="text-slate-400 text-xs uppercase tracking-wide">Site</span>
                <br /><span className="text-slate-600">{site_name}</span>
              </div>
            )}
            {site_address && (
              <div className="text-right">
                <span className="text-slate-400 text-xs uppercase tracking-wide">Address</span>
                <br /><span className="text-slate-600">{site_address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white shadow-sm px-8 py-6">
          <h3 className="text-sm font-bold uppercase text-slate-700 mb-3 tracking-wide">Scope of Work</h3>
          {lineItems.length > 0 ? (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-700 text-white">
                  <th className="text-left px-3 py-2 font-semibold">Description</th>
                  <th className="text-center px-3 py-2 font-semibold w-16">Qty</th>
                  {coBreakout && <th className="text-right px-3 py-2 font-semibold w-28" data-testid="co-review-col-material">Material</th>}
                  {coBreakout && <th className="text-right px-3 py-2 font-semibold w-24" data-testid="co-review-col-freight">Freight</th>}
                  {coBreakout && <th className="text-right px-3 py-2 font-semibold w-28" data-testid="co-review-col-labor">Labor</th>}
                  {!coBreakout && <th className="text-right px-3 py-2 font-semibold w-28">Unit Price</th>}
                  <th className="text-right px-3 py-2 font-semibold w-28">Total</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, idx) => (
                  <tr key={li.id || idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                    <td className="px-3 py-2 text-slate-700">
                      <div>{li.name || li.description || "—"}</div>
                      {li.install_description && (
                        <div className="text-xs text-slate-500 italic mt-0.5" data-testid={`co-li-install-desc-${idx}`}>
                          {li.install_description}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">{li.quantity}</td>
                    {coBreakout && <td className="px-3 py-2 text-right tabular-nums" data-testid={`co-review-material-${idx}`}>{fmt(li.material_total || 0)}</td>}
                    {coBreakout && <td className="px-3 py-2 text-right tabular-nums" data-testid={`co-review-freight-${idx}`}>{fmt(li.freight_total || 0)}</td>}
                    {coBreakout && <td className="px-3 py-2 text-right tabular-nums" data-testid={`co-review-labor-${idx}`}>{fmt(li.labor_total || 0)}</td>}
                    {!coBreakout && <td className="px-3 py-2 text-right tabular-nums">{fmt(li.unit_price)}</td>}
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(li.total || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-slate-400 py-4 text-center">No line items</p>
          )}

          <div className="flex justify-end mt-4 pt-3 border-t">
            <div className="text-right space-y-1">
              <div className="text-sm text-slate-600 flex justify-between gap-6">
                <span>Subtotal:</span>
                <span className="tabular-nums font-medium" data-testid="co-subtotal">{fmt(co.hide_tax ? subtotal + taxAmount : subtotal)}</span>
              </div>
              {/* iter310 — hide-tax job: no tax line on the customer's page. */}
              {!co.hide_tax && (
                <div className="text-sm text-slate-600 flex justify-between gap-6">
                  <span>Tax ({taxRate}%):</span>
                  <span className="tabular-nums font-medium" data-testid="co-tax">{fmt(taxAmount)}</span>
                </div>
              )}
              <div className="text-lg font-bold text-slate-800 flex justify-between gap-6 border-t pt-1">
                <span>Change Order Total:</span>
                <span className="tabular-nums" data-testid="co-total">{fmt(grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Response section */}
        <div className="bg-white rounded-b-xl shadow-sm px-8 py-6 border-t">
          {responded === "approved" && (
            <div className="text-center py-4 space-y-3" data-testid="co-approved-msg">
              <div className="flex items-center gap-3 justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <h3 className="text-lg font-bold text-green-700">Approved & Executed</h3>
                  <p className="text-sm text-slate-500">
                    This change order has been approved and signed
                    {co.signed_by ? ` by ${co.signed_by}` : ""}
                    {co.signed_at ? ` on ${format(new Date(co.signed_at), "MMM d, yyyy")}` : ""}.
                  </p>
                </div>
              </div>
              {co.signature_data && (
                <div className="pt-2">
                  <img src={co.signature_data} alt="Signature" className="h-16 border rounded mx-auto" />
                </div>
              )}
              {co.executed_co_pdf && (
                <Button
                  size="sm"
                  className="bg-slate-800 hover:bg-slate-900 text-white shadow"
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = co.executed_co_pdf;
                    link.download = `${co.co_number}_Executed.pdf`;
                    link.click();
                  }}
                  data-testid="btn-download-executed-co"
                >
                  <FileDown className="w-4 h-4 mr-1" /> Download Signed Change Order
                </Button>
              )}
            </div>
          )}
          {(responded === "declined" || responded === "not_approved") && (
            <div className="flex items-center gap-3 py-4 text-center justify-center" data-testid="co-declined-msg">
              <XCircle className="w-8 h-8 text-red-400" />
              <div>
                <h3 className="text-lg font-bold text-red-600">{responded === "not_approved" ? "Not Approved" : "Declined"}</h3>
                <p className="text-sm text-slate-500">This change order has been {responded === "not_approved" ? "not approved" : "declined"}.</p>
              </div>
            </div>
          )}
          {!responded && (
            <>
              {showSignature ? (
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-slate-700">Sign to Approve</h3>
                  <p className="text-xs text-slate-500">By signing below, you approve this change order and authorize the work described above.</p>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Your Name</label>
                    <Input value={signedBy} onChange={e => setSignedBy(e.target.value)} placeholder="Full name" className="max-w-xs mt-1" data-testid="co-signed-by-input" />
                  </div>
                  <SignaturePad onSave={handleApproveWithSignature} />
                  <Button variant="outline" onClick={() => setShowSignature(false)} disabled={responding} className="text-sm">
                    Back
                  </Button>
                </div>
              ) : showDeclineForm ? (
                <div className="space-y-3">
                  <Textarea
                    placeholder="Reason for declining (optional)"
                    value={declineReason}
                    onChange={e => setDeclineReason(e.target.value)}
                    className="text-sm"
                    data-testid="decline-reason-input"
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowDeclineForm(false)} disabled={responding}>Cancel</Button>
                    <Button variant="destructive" onClick={handleDecline} disabled={responding} data-testid="btn-confirm-decline">
                      {responding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <XCircle className="w-4 h-4 mr-1" />}
                      Confirm Decline
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600 mb-4">Please review the change order above. To approve, you will be asked to sign.</p>
                  <div className="flex gap-3">
                    <Button className="bg-green-600 hover:bg-green-700 flex-1 py-6 text-base" onClick={() => setShowSignature(true)} disabled={responding} data-testid="btn-approve-co">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Approve & Sign
                    </Button>
                    <Button variant="outline" className="flex-1 py-6 text-base text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDeclineForm(true)} disabled={responding} data-testid="btn-decline-co">
                      <XCircle className="w-5 h-5 mr-2" />
                      Decline
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
